import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ChannelAnalyticsSummary, AnalyticsSummary, CountryStat } from '../lib/api'
import HudLoader from '../components/HudLoader'
import {
  TrendingUp,
  DollarSign,
  Eye,
  Users,
  Clock,
  Share2,
  MessageSquare,
  Loader2,
  RefreshCw,
  Calendar,
  Download,
} from 'lucide-react'
import { format, subDays } from 'date-fns'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts'

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

function MetricCard({ title, value, icon: Icon, color, change }: {
  title: string
  value: string | number
  icon: React.ComponentType<any>
  color: string
  change?: string
}) {
  return (
    <div className="metric-card">
      <div className="metric-icon" style={{ backgroundColor: `${color}15`, color }}>
        <Icon size={24} />
      </div>
      <div className="metric-value">{value}</div>
      <div className="metric-title">{title}</div>
      {change && <div className="metric-change positive">{change}</div>}
    </div>
  )
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="chart-card">
      <div className="chart-header">
        <h3>{title}</h3>
      </div>
      <div className="chart-container">{children}</div>
    </div>
  )
}

export default function Analytics() {
  const queryClient = useQueryClient()
  const [selectedChannel, setSelectedChannel] = useState<string>('')
  const [days, setDays] = useState(30)

  const { data: channels } = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<{ id: string; title: string }[]>('/channels'),
  })

  // "__all__" = har channel ka alag-alag total + combined (user mandate).
  const ALL = '__all__'
  const isAll = selectedChannel === ALL
  const isChannel = !!selectedChannel && !isAll

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['analytics', 'channel', selectedChannel, days],
    queryFn: () => api.get<ChannelAnalyticsSummary>(`/analytics/channel/${selectedChannel}/summary?days=${days}`),
    enabled: isChannel,
  })

  // ASLI daily series (video_analytics rows) — random demo data ab nahi.
  const { data: daily } = useQuery({
    queryKey: ['analytics', 'daily', selectedChannel, days],
    queryFn: () => api.get<{ date: string; views: number; watch_time_minutes: number; likes: number; revenue: number }[]>(`/analytics/channel/${selectedChannel}/daily?days=${days}`),
    enabled: isChannel,
  })

  // Har channel ka alag total (views/watch-time/revenue) — ek jagah.
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['analytics', 'overview', days],
    queryFn: () => api.get<Array<{ channel_id: string; title: string; total_views: number; total_watch_time_minutes: number; total_revenue: number; total_likes: number; total_videos: number; subscriber_count: number; yt_lifetime_views: number; yt_video_count: number }>>(`/analytics/overview?days=${days}`),
    enabled: isAll,
  })

  // Video titles (UUID ki jagah asli naam dikhe)
  const { data: videos } = useQuery({
    queryKey: ['videos', 'all'],
    queryFn: () => api.get<{ id: string; title: string }[]>('/videos?limit=300'),
  })
  const titleOf = (id: string) => videos?.find((v: any) => v.id === id)?.title || id

  const syncMutation = useMutation({
    mutationFn: (channelId: string) => api.post<{ note?: string; message?: string }>(`/analytics/channel/${channelId}/sync`, { days }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['analytics'] }),
  })

  // Overview mode: ek click = teeno channels ki sync
  const syncAll = () => {
    channels?.forEach((c: any) => syncMutation.mutate(c.id))
  }

  const dailyData = (daily || []).map((r) => ({
    date: format(new Date(`${r.date}T00:00:00`), 'MMM dd'),
    views: r.views,
    revenue: r.revenue,
    watchTime: r.watch_time_minutes,
  }))

  // Sirf asli data — fallback me random numbers dikhana ek jhooth tha.
  const topVideos = (analytics?.top_videos?.slice(0, 10) || []).map((v) => ({
    ...v,
    label: titleOf(v.video_id),
  }))

  if (!selectedChannel) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Analytics</h1>
          <p>Track performance metrics and revenue across your channels</p>
        </div>
        <div className="card">
          <div className="empty-state">
            <TrendingUp size={48} />
            <h3>Select a channel</h3>
            <p>Choose a channel from the dropdown to view analytics</p>
            <select
              value={selectedChannel}
              onChange={(e) => setSelectedChannel(e.target.value)}
              className="select"
              style={{ maxWidth: 300, marginTop: 16 }}
            >
              <option value="">Select Channel</option>
              <option value="__all__">All Channels (Total)</option>
              {channels?.map((c: any) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Analytics</h1>
          <p>{isAll ? 'Combined total + har channel ka alag performance' : `Performance metrics for ${channels?.find((c: any) => c.id === selectedChannel)?.title || 'selected channel'}`}</p>
        </div>
        <div className="header-actions">
          <select value={selectedChannel} onChange={(e) => setSelectedChannel(e.target.value)} className="select" style={{ minWidth: 250 }}>
            <option value="__all__">All Channels (Total)</option>
            {channels?.map((c: any) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))} className="select" style={{ width: 140 }}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button className="btn btn-secondary" onClick={() => isAll ? syncAll() : syncMutation.mutate(selectedChannel)} disabled={syncMutation.isPending}>
            {syncMutation.isPending ? <Loader2 size={16} className="spin" /> : <> <RefreshCw size={16} /> {isAll ? 'Sync All' : 'Sync Now'}</>}
          </button>
        </div>
      </div>

      {syncMutation.data?.note && (
        <div className="card mt-4" style={{ borderLeft: '3px solid #f59e0b' }}>
          <strong>Scope note:</strong> {syncMutation.data.note}
        </div>
      )}

      {isAll ? (
        overviewLoading ? <HudLoader text="Loading totals" /> : overview ? (
          <>
            <div className="metrics-grid">
              <MetricCard title="Total Views (All)" value={(overview.find((r) => r.channel_id === 'total')?.total_views || 0).toLocaleString()} icon={Eye} color="#3b82f6" />
              <MetricCard title="Total Watch Time" value={`${((overview.find((r) => r.channel_id === 'total')?.total_watch_time_minutes || 0) / 60).toFixed(1)}h`} icon={Clock} color="#8b5cf6" />
              <MetricCard title="Total Revenue" value={`$${(overview.find((r) => r.channel_id === 'total')?.total_revenue || 0).toFixed(2)}`} icon={DollarSign} color="#10b981" />
              <MetricCard title="Total Subscribers" value={(overview.find((r) => r.channel_id === 'total')?.subscriber_count || 0).toLocaleString()} icon={Users} color="#f59e0b" />
            </div>

            <div className="card mt-4">
              <div className="card-header"><h2>Har channel ka total</h2></div>
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>Channel</th>
                      <th title="Synced analytics rows — automation ki videos (abhi tak mostly unlisted)">Synced Views</th>
                      <th title="YouTube ka official lifetime total — channel ke SARE videos (purane bhi)">YT Lifetime Views</th>
                      <th>Watch Time</th>
                      <th>Est. Revenue</th>
                      <th>Likes</th>
                      <th>Subscribers</th>
                      <th>Videos (YT)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.filter((r) => r.channel_id !== 'total').map((r) => (
                      <tr key={r.channel_id}>
                        <td><strong>{r.title}</strong></td>
                        <td>{r.total_views.toLocaleString()}</td>
                        <td>{r.yt_lifetime_views.toLocaleString()} <span className="muted">({r.yt_video_count} videos)</span></td>
                        <td>{(r.total_watch_time_minutes / 60).toFixed(1)}h</td>
                        <td>${r.total_revenue.toFixed(2)}</td>
                        <td>{r.total_likes.toLocaleString()}</td>
                        <td>{r.subscriber_count.toLocaleString()}</td>
                        <td>{r.yt_video_count}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {overview.filter((r) => r.channel_id === 'total').map((r) => (
                      <tr key="total" style={{ borderTop: '2px solid #3b82f6', fontWeight: 700 }}>
                        <td>TOTAL</td>
                        <td>{r.total_views.toLocaleString()}</td>
                        <td>{r.yt_lifetime_views.toLocaleString()} <span className="muted">({r.yt_video_count} videos)</span></td>
                        <td>{(r.total_watch_time_minutes / 60).toFixed(1)}h</td>
                        <td>${r.total_revenue.toFixed(2)}</td>
                        <td>{r.total_likes.toLocaleString()}</td>
                        <td>{r.subscriber_count.toLocaleString()}</td>
                        <td>{r.yt_video_count}</td>
                      </tr>
                    ))}
                  </tfoot>
                </table>
              </div>
              <p className="muted" style={{ marginTop: 10 }}>
                <strong>Synced Views</strong> = automation ki videos ki asli analytics rows (abhi tak mostly unlisted, isliye kam). <strong>YT Lifetime Views</strong> = YouTube ka official all-time total — isme channel ke purane videos bhi hain (Channels page par jo dikhta hai wahi). Naya data lane ke liye upar <strong>Sync All</strong> dabao.
              </p>
            </div>
          </>
        ) : null
      ) : isLoading ? (
        <HudLoader text="Loading analytics" />
      ) : analytics ? (
        <>
          <div className="metrics-grid">
            <MetricCard title="Total Views" value={analytics.total_views.toLocaleString()} icon={Eye} color="#3b82f6" />
            <MetricCard title="Watch Time" value={`${(analytics.total_watch_time_minutes / 60).toFixed(1)}h`} icon={Clock} color="#8b5cf6" />
            <MetricCard title="Est. Revenue" value={`$${analytics.total_estimated_revenue_usd.toFixed(2)}`} icon={DollarSign} color="#10b981" />
            <MetricCard title="Subscribers (Net)" value={analytics.top_videos.reduce((sum, v) => sum + v.net_subscribers, 0).toLocaleString()} icon={Users} color="#f59e0b" />
            <MetricCard title="Avg CTR" value={`${analytics.avg_ctr.toFixed(2)}%`} icon={Share2} color="#ec4899" />
            <MetricCard title="Videos" value={analytics.total_videos} icon={MessageSquare} color="#6366f1" />
          </div>

          <div className="charts-grid">
            <ChartCard title="Daily Views & Revenue">
              {dailyData.length === 0 ? (
                <div className="empty-state" style={{ padding: 40 }}>
                  <p>Abhi koi daily data nahi — Sync Now dabao (ya pehli sync ka intezaar karo).</p>
                </div>
              ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dailyData}>
                  <defs>
                    <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                  <Tooltip labelFormatter={(v) => format(new Date(v), 'MMM dd, yyyy')} />
                  <Area type="monotone" dataKey="views" yAxisId="left" stroke="#3b82f6" fill="url(#viewsGrad)" />
                  <Area type="monotone" dataKey="revenue" yAxisId="right" stroke="#10b981" fill="url(#revGrad)" />
                </AreaChart>
              </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Top Videos by Views">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topVideos} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="label" width={120} stroke="#9ca3af" tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value: number) => [value.toLocaleString(), 'Views']} />
                  <Bar dataKey="total_views" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Watch Time Distribution">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={topVideos}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="total_watch_time_minutes"
                    nameKey="label"
                    label={({ label, percent }) => `${String(label).slice(0, 18)} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {topVideos.map((_, i) => <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value: number) => [value.toLocaleString(), 'Minutes']} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Revenue by Video">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topVideos} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="label" width={120} stroke="#9ca3af" tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value: number) => [`$${value.toFixed(2)}`, 'Revenue']} />
                  <Bar dataKey="estimated_revenue_usd" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="card mt-4">
            <h2>Top Performing Videos</h2>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Video</th>
                    <th>Views</th>
                    <th>Watch Time</th>
                    <th>Likes</th>
                    <th>Comments</th>
                    <th>Shares</th>
                    <th>Net Subs</th>
                    <th>Revenue</th>
                    <th>CTR</th>
                  </tr>
                </thead>
                <tbody>
                  {topVideos.length === 0 && (
                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: 20 }}>
                      Abhi koi analytics row nahi — upar <strong>Sync Now</strong> dabao (ya pehli sync ka intezaar karo).
                    </td></tr>
                  )}
                  {topVideos.map((video) => (
                    <tr key={video.video_id}>
                      <td><span className="video-title">{titleOf(video.video_id)}</span></td>
                      <td>{video.total_views.toLocaleString()}</td>
                      <td>{(video.total_watch_time_minutes / 60).toFixed(1)}h</td>
                      <td>{video.total_likes.toLocaleString()}</td>
                      <td>{video.total_comments.toLocaleString()}</td>
                      <td>{video.total_shares.toLocaleString()}</td>
                      <td className="positive">+{video.net_subscribers}</td>
                      <td>${video.estimated_revenue_usd.toFixed(2)}</td>
                      <td>{video.avg_ctr.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <TopCountries channelId={selectedChannel} days={days} />
        </>
      ) : null}
    </div>
  )
}

function TopCountries({ channelId, days }: { channelId: string; days: number }) {
  const { data: countries, isLoading } = useQuery({
    queryKey: ['analytics', 'countries', channelId, days],
    queryFn: () => api.get<CountryStat[]>(`/analytics/channel/${channelId}/countries?days=${days}`),
    enabled: !!channelId,
  })
  if (isLoading) return <HudLoader text="Loading countries" />
  if (!countries?.length) return null
  return (
    <div className="card mt-4">
      <h2>Top Countries — kaun sa video kaha dekha gaya</h2>
      <div className="chart-container" style={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={countries.slice(0, 10)} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" stroke="#9ca3af" tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="country" width={60} stroke="#9ca3af" tick={{ fontSize: 12 }} />
            <Tooltip formatter={(value: number) => [value.toLocaleString(), 'Views']} />
            <Bar dataKey="views" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Country</th>
              <th>Video</th>
              <th>Views</th>
              <th>Watch Time</th>
              <th>Subs +</th>
            </tr>
          </thead>
          <tbody>
            {countries.map((c, i) => (
              <tr key={`${c.country}-${c.video_id}-${i}`}>
                <td><span className="badge badge-info">{c.country}</span></td>
                <td><span className="video-title">{c.title || c.video_id}</span></td>
                <td>{c.views.toLocaleString()}</td>
                <td>{(c.watch_time_minutes / 60).toFixed(1)}h</td>
                <td className="positive">+{c.subscribers_gained}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
